import React from 'react';
import { ArrowUp } from 'lucide-react';

/**
 * Small inline link that smoothly scrolls back to the top of a target section.
 *
 * Props:
 *  - targetId: string  (the element id to scroll to). Use 'top' to go to the page top.
 *  - label:    string  (visible text). Defaults to "Back to top".
 *  - variant:  'dark' | 'light'  (color treatment to match section background).
 *  - className: extra classes for the wrapper alignment.
 */
const BackToTop = ({ targetId, label = 'Back to top', variant = 'light', className = '' }) => {
  const handleClick = (event) => {
    event.preventDefault();
    if (!targetId || targetId === 'top') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    const el = document.getElementById(targetId);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const isDark = variant === 'dark';
  const base =
    'group inline-flex items-center gap-2 text-[11px] tracking-[0.28em] uppercase transition-colors duration-200';
  const colors = isDark
    ? 'text-white/55 hover:text-[#c9a961]'
    : 'text-[#5b6b7d] hover:text-[#c9a961]';

  return (
    <div className={`flex justify-center ${className}`}>
      <a href={`#${targetId || 'top'}`} onClick={handleClick} className={`${base} ${colors}`}>
        <span
          className={`w-9 h-9 flex items-center justify-center border transition-colors duration-200 ${
            isDark
              ? 'border-white/20 group-hover:border-[#c9a961]'
              : 'border-[#0c1a2b]/20 group-hover:border-[#c9a961]'
          }`}
        >
          <ArrowUp size={14} />
        </span>
        <span>{label}</span>
      </a>
    </div>
  );
};

export default BackToTop;
